import moment from 'moment';
import * as XLSX from 'xlsx';

const isNum = ref => `ISNUMBER(${ref})`;

// Excel export columns, in WIP-tab order, with Excel number/date cell formats so
// values display formatted but stay numeric (computable in Excel).
// Derived columns carry a `formula` built from the row's cell refs, so the sheet
// recalculates when someone edits an input in Excel. Each formula yields "" when
// its inputs are missing, mirroring the blanks the app shows for those rows.
export const EXCEL_COLUMNS = [
  { key: 'name', header: 'Project Description', type: 'text' },
  { key: 'budget', header: 'Contract Price', type: 'money' },
  { key: 'totalApprovedHours', header: 'Actual Hours Worked', type: 'hours' },
  { key: 'monthToDateHours', header: 'Month To Date', type: 'hours' },
  { key: 'actualHoursRemaining', header: 'Actual Hours Remaining', type: 'hours' },
  {
    key: 'totalHours',
    header: 'Total Hours',
    type: 'hours',
    formula: ({ totalApprovedHours: worked, actualHoursRemaining: remaining }) =>
      `IF(AND(${isNum(worked)},${isNum(remaining)}),${worked}+${remaining},"")`,
  },
  {
    key: 'percentComplete',
    header: '% Complete',
    type: 'percent',
    formula: ({ totalApprovedHours: worked, totalHours: total }) =>
      `IF(AND(${isNum(total)},${total}>0),${worked}/${total},"")`,
  },
  { key: 'estimatedEndDate', header: 'Date of Completion', type: 'date' },
  { key: 'totalBilledAmount', header: 'Billed To Date', type: 'money' },
  {
    key: 'incomeRecognized',
    header: 'Income Recognized',
    type: 'money',
    formula: ({ budget: contract, percentComplete: pct }) =>
      `IF(AND(${isNum(contract)},${isNum(pct)}),${contract}*${pct},"")`,
  },
  {
    key: 'backlog',
    header: 'Backlog',
    type: 'money',
    formula: ({ budget: contract, incomeRecognized: income }) =>
      `IF(${isNum(income)},${contract}-${income},"")`,
  },
  {
    key: 'overUnderBilled',
    header: 'Over/Under Billed',
    type: 'money',
    formula: ({ totalBilledAmount: billed, incomeRecognized: income }) =>
      `IF(AND(${isNum(billed)},${isNum(income)}),${billed}-${income},"")`,
  },
  { key: 'owner', header: 'Project Managers', type: 'text' },
];

const EXCEL_FORMATS = {
  money: '$#,##0.00',
  hours: '#,##0.00',
  percent: '0.00%',
  date: 'mm/dd/yyyy',
};

export const buildWipSheet = rows => {
  const ws = {};
  EXCEL_COLUMNS.forEach((col, c) => {
    ws[XLSX.utils.encode_cell({ r: 0, c })] = { t: 's', v: col.header };
  });
  rows.forEach((row, i) => {
    const r = i + 1;
    const refs = Object.fromEntries(
      EXCEL_COLUMNS.map((col, c) => [col.key, XLSX.utils.encode_cell({ r, c })])
    );
    EXCEL_COLUMNS.forEach(col => {
      const value = row[col.key];
      const addr = refs[col.key];
      if (col.formula) {
        // Cache the app's computed value so the sheet reads correctly even
        // before Excel recalculates.
        ws[addr] =
          typeof value === 'number'
            ? { t: 'n', v: value, f: col.formula(refs), z: EXCEL_FORMATS[col.type] }
            : { t: 's', v: '', f: col.formula(refs), z: EXCEL_FORMATS[col.type] };
        return;
      }
      if (value == null || value === '') return; // leave blank cells empty
      if (col.type === 'text') {
        ws[addr] = { t: 's', v: String(value) };
      } else if (col.type === 'date') {
        // Date scalar is epoch-ms at UTC midnight; SheetJS reads local date parts,
        // so build a local-midnight date from the UTC calendar parts to avoid an
        // off-by-one day west of UTC (matches the grid's moment.utc formatting).
        const u = moment.utc(value);
        if (u.isValid()) {
          const d = new Date(u.year(), u.month(), u.date());
          ws[addr] = { t: 'd', v: d, z: EXCEL_FORMATS.date };
        }
      } else if (typeof value === 'number') {
        ws[addr] = { t: 'n', v: value, z: EXCEL_FORMATS[col.type] };
      }
    });
  });
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: rows.length, c: EXCEL_COLUMNS.length - 1 },
  });
  ws['!cols'] = EXCEL_COLUMNS.map(col => ({ wch: Math.max(col.header.length + 2, 14) }));
  return ws;
};

export const exportWipExcel = (rows, month) => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildWipSheet(rows), 'WIP');
  XLSX.writeFile(wb, `WIP-${month}.xlsx`, { cellDates: true });
};
